import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import IdentityAssistPanel from "../components/IdentityAssistPanel";

interface Zone {
  name: string;
  type?: string;
  ds_integrated?: boolean;
  reverse?: boolean;
  auto_created?: boolean;
  dynamic_update?: string;
}
interface Rec {
  name: string;
  type: string;
  data: string;
  ttl?: number;
}

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm";
const WRITABLE = ["A", "AAAA", "CNAME", "MX", "TXT", "PTR"] as const;
const DATA_HINT: Record<string, string> = {
  A: "192.0.2.10",
  AAAA: "2001:db8::10",
  CNAME: "target.contoso.com",
  MX: "10 mail.contoso.com",
  TXT: "v=spf1 include:… -all",
  PTR: "host.contoso.com",
};

function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || fallback;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * DNS management (#18). Runs on the customer's AD runner (a DC with the DNS role)
 * through the async command rail: POST queues a dns_* command, then we poll
 * GET /commands/{id} until the agent (which checks in every ~20s) returns a
 * result. Scoped by the global customer switcher.
 */
export default function ToolsDns() {
  const { currentCustomerId, current } = useCustomer();
  const [zones, setZones] = useState<Zone[] | null>(null);
  const [zone, setZone] = useState<string>("");
  const [records, setRecords] = useState<Rec[] | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // add-record form
  const [nName, setNName] = useState("");
  const [nType, setNType] = useState<string>("A");
  const [nData, setNData] = useState("");
  const [nTtl, setNTtl] = useState(3600);
  // inline record edit
  const [editKey, setEditKey] = useState("");
  const [eData, setEData] = useState("");
  const [eTtl, setETtl] = useState(3600);
  // new-zone form
  const [showNewZone, setShowNewZone] = useState(false);
  const [zName, setZName] = useState("");
  const [zReverse, setZReverse] = useState(false);
  const [zNetwork, setZNetwork] = useState("");
  const [zDyn, setZDyn] = useState("Secure");
  const recSort = useSort("name");

  // Start a dns_* command and poll it to completion; returns the result object.
  const runCommand = useCallback(
    async (start: () => Promise<{ data: { command_id: number } }>): Promise<any> => {
      const cid = currentCustomerId;
      const r = await start();
      const id = r.data.command_id;
      const deadline = Date.now() + 60000;
      while (Date.now() < deadline) {
        await sleep(2500);
        const p = await api.get(`/api/v1/dns/${cid}/commands/${id}`);
        const st = p.data.status;
        if (st === "completed") return p.data.result || {};
        if (st === "failed" || st === "expired")
          throw new Error(p.data.error_message || `Command ${st}`);
      }
      throw new Error("Timed out waiting for the AD runner (is it online?).");
    },
    [currentCustomerId],
  );

  const loadZones = useCallback(async () => {
    if (currentCustomerId == null) return;
    setBusy("zones");
    setError("");
    setZones(null);
    setZone("");
    setRecords(null);
    try {
      const res = await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/zones/refresh`),
      );
      setZones(res.zones || []);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load zones"));
    } finally {
      setBusy("");
    }
  }, [currentCustomerId, runCommand]);

  const loadRecords = useCallback(
    async (z: string) => {
      setBusy("records");
      setError("");
      setZone(z);
      setRecords(null);
      try {
        const res = await runCommand(() =>
          api.post(`/api/v1/dns/${currentCustomerId}/records/refresh`, { zone: z }),
        );
        setRecords(res.records || []);
      } catch (e) {
        setError(errText(e, (e as Error).message || "Couldn't load records"));
      } finally {
        setBusy("");
      }
    },
    [currentCustomerId, runCommand],
  );

  const addRecord = async () => {
    if (!nName.trim() || !nData.trim()) return;
    setBusy("add");
    setError("");
    try {
      await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/records`, {
          zone,
          name: nName.trim(),
          record_type: nType,
          data: nData.trim(),
          ttl: nTtl,
        }),
      );
      setNName("");
      setNData("");
      await loadRecords(zone);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Create failed"));
      setBusy("");
    }
  };

  const saveEdit = async (r: Rec) => {
    setBusy("edit");
    setError("");
    try {
      await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/records/update`, {
          zone,
          name: r.name,
          record_type: r.type,
          old_data: r.data,
          new_data: eData.trim(),
          ttl: eTtl,
        }),
      );
      setEditKey("");
      await loadRecords(zone);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Edit failed"));
      setBusy("");
    }
  };

  const addZone = async () => {
    setBusy("zone");
    setError("");
    try {
      await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/zones`, {
          name: zName.trim(),
          reverse: zReverse,
          network_id: zNetwork.trim(),
          dynamic_update: zDyn,
        }),
      );
      setShowNewZone(false);
      setZName("");
      setZNetwork("");
      await loadZones();
    } catch (e) {
      setError(errText(e, (e as Error).message || "Create zone failed"));
      setBusy("");
    }
  };

  const setZoneDyn = async (zoneName: string, dyn: string) => {
    setBusy("zonedyn");
    setError("");
    try {
      await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/zones/update`, {
          name: zoneName,
          dynamic_update: dyn,
        }),
      );
      await loadZones();
      setZone(zoneName);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Update zone failed"));
      setBusy("");
    }
  };

  const delRecord = async (r: Rec) => {
    if (!window.confirm(`Delete ${r.type} record "${r.name}" → ${r.data}?`)) return;
    setBusy("del");
    setError("");
    try {
      await runCommand(() =>
        api.post(`/api/v1/dns/${currentCustomerId}/records/delete`, {
          zone,
          name: r.name,
          record_type: r.type,
          data: r.data,
        }),
      );
      await loadRecords(zone);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Delete failed"));
      setBusy("");
    }
  };

  useEffect(() => {
    setZones(null);
    setZone("");
    setRecords(null);
    setError("");
  }, [currentCustomerId]);

  if (currentCustomerId == null) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">DNS</h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 italic mt-4">
          Select a customer in the header to manage its DNS.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">DNS</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Zones and records on {current?.name}'s DNS server, via its AD runner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {zones && (
            <button
              onClick={() => setShowNewZone((v) => !v)}
              disabled={!!busy}
              className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              + New zone
            </button>
          )}
          <button
            onClick={loadZones}
            disabled={!!busy}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === "zones" ? "Loading…" : zones ? "Reload zones" : "Load zones"}
          </button>
        </div>
      </div>

      <IdentityAssistPanel customerId={currentCustomerId} scope="dns" />

      {showNewZone && (
        <div className={`${CARD} p-4 mb-3`}>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={zReverse} onChange={(e) => setZReverse(e.target.checked)} />
              Reverse zone
            </label>
            {zReverse ? (
              <input
                value={zNetwork}
                onChange={(e) => setZNetwork(e.target.value)}
                placeholder="Network ID (e.g. 192.168.15.0/24)"
                className={`${INPUT} w-64`}
              />
            ) : (
              <input
                value={zName}
                onChange={(e) => setZName(e.target.value)}
                placeholder="Zone name (e.g. contoso.local)"
                className={`${INPUT} w-64`}
              />
            )}
            <select value={zDyn} onChange={(e) => setZDyn(e.target.value)} className={INPUT} title="Dynamic updates">
              <option value="Secure">Secure only</option>
              <option value="NonsecureAndSecure">Nonsecure and secure</option>
              <option value="None">None</option>
            </select>
            <button
              onClick={addZone}
              disabled={!!busy || (zReverse ? !zNetwork.trim() : !zName.trim())}
              className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Create AD-integrated zone
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}
      {busy && !error && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <span className="h-4 w-4 shrink-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
          <div>
            <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Working…</div>
            <div className="text-xs text-blue-700 dark:text-blue-300">
              Waiting for {current?.name}'s on-site runner to run the command — usually a few
              seconds, up to ~20s.
            </div>
          </div>
        </div>
      )}

      {zones && (
        <div className="grid gap-4 md:grid-cols-[16rem,1fr]">
          {/* Zone list, grouped Forward vs Reverse (like the DNS console) */}
          <div className="self-start space-y-4">
            {(() => {
              const fwd = zones.filter((z) => !z.reverse);
              const rev = zones.filter((z) => z.reverse);
              const group = (label: string, list: Zone[]) => (
                <div className={`${CARD} divide-y divide-gray-100 dark:divide-gray-700`}>
                  <div className="px-3 py-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    {label} ({list.length})
                  </div>
                  {list.length === 0 && (
                    <p className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500">None.</p>
                  )}
                  {list.map((z) => (
                    <button
                      key={z.name}
                      onClick={() => loadRecords(z.name)}
                      title={`${z.type || ""}${z.ds_integrated ? " · AD-integrated" : ""}${z.auto_created ? " · auto-created" : ""}`}
                      className={`w-full text-left px-3 py-2 text-sm break-all hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                        zone === z.name
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium"
                          : "text-gray-800 dark:text-gray-100"
                      }`}
                    >
                      {z.name}
                      {z.auto_created && (
                        <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500">auto</span>
                      )}
                    </button>
                  ))}
                </div>
              );
              return (
                <>
                  {group("Forward lookup zones", fwd)}
                  {group("Reverse lookup zones", rev)}
                </>
              );
            })()}
          </div>

          {/* Records */}
          <div className={`${CARD} p-4`}>
            {!zone ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                Pick a zone to view its records.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{zone}</h3>
                  {(() => {
                    const sel = (zones || []).find((z) => z.name === zone);
                    if (!sel || sel.reverse === undefined) return null;
                    return (
                      <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                        Dynamic updates
                        <select
                          value={sel.dynamic_update || "Secure"}
                          onChange={(e) => setZoneDyn(zone, e.target.value)}
                          disabled={!!busy}
                          className={`${INPUT} py-1`}
                        >
                          <option value="Secure">Secure only</option>
                          <option value="NonsecureAndSecure">Nonsecure and secure</option>
                          <option value="None">None</option>
                        </select>
                      </label>
                    );
                  })()}
                </div>

                {/* Add record */}
                <div className="flex flex-wrap items-end gap-2 mb-4">
                  <input
                    value={nName}
                    onChange={(e) => setNName(e.target.value)}
                    placeholder="name (e.g. www or @)"
                    className={`${INPUT} w-40`}
                  />
                  <select value={nType} onChange={(e) => setNType(e.target.value)} className={INPUT}>
                    {WRITABLE.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <input
                    value={nData}
                    onChange={(e) => setNData(e.target.value)}
                    placeholder={DATA_HINT[nType] || "data"}
                    className={`${INPUT} w-56`}
                  />
                  <input
                    type="number"
                    value={nTtl}
                    onChange={(e) => setNTtl(Number(e.target.value) || 3600)}
                    title="TTL (seconds)"
                    className={`${INPUT} w-24`}
                  />
                  <button
                    onClick={addRecord}
                    disabled={!!busy || !nName.trim() || !nData.trim()}
                    className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>

                <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                      <tr>
                        <SortableTh label="Name" sortKey="name" sort={recSort.sort} onSort={recSort.toggle} />
                        <SortableTh label="Type" sortKey="type" sort={recSort.sort} onSort={recSort.toggle} />
                        <SortableTh label="Data" sortKey="data" sort={recSort.sort} onSort={recSort.toggle} />
                        <SortableTh label="TTL" sortKey="ttl" sort={recSort.sort} onSort={recSort.toggle} align="right" />
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {sortRows(records || [], recSort.sort, (r, k) =>
                        k === "ttl" ? (r.ttl ?? 0) : String((r as any)[k] ?? "").toLowerCase(),
                      ).map((r, i) => {
                        const editable = (WRITABLE as readonly string[]).includes(r.type);
                        const key = `${r.name}|${r.type}|${r.data}`;
                        const isEditing = editKey === key;
                        return (
                          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                            <td className="px-3 py-1.5 text-gray-800 dark:text-gray-100">{r.name}</td>
                            <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{r.type}</td>
                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 break-all">
                              {isEditing ? (
                                <input
                                  value={eData}
                                  onChange={(e) => setEData(e.target.value)}
                                  className={`${INPUT} w-full`}
                                />
                              ) : (
                                r.data
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-500 dark:text-gray-400">
                              {isEditing ? (
                                <input
                                  type="number"
                                  value={eTtl}
                                  onChange={(e) => setETtl(Number(e.target.value) || 3600)}
                                  className={`${INPUT} w-20`}
                                />
                              ) : (
                                (r.ttl ?? "—")
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right whitespace-nowrap">
                              {editable &&
                                (isEditing ? (
                                  <span className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => saveEdit(r)}
                                      disabled={!!busy || !eData.trim()}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditKey("")}
                                      className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => {
                                        setEditKey(key);
                                        setEData(r.data);
                                        setETtl(r.ttl ?? 3600);
                                      }}
                                      disabled={!!busy}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => delRecord(r)}
                                      disabled={!!busy}
                                      className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                    >
                                      Delete
                                    </button>
                                  </span>
                                ))}
                            </td>
                          </tr>
                        );
                      })}
                      {records === null && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">
                            <span className="inline-flex items-center gap-2">
                              <span className="h-3.5 w-3.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                              Loading records…
                            </span>
                          </td>
                        </tr>
                      )}
                      {records && records.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">
                            No records.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
